import localforage from 'localforage';
import { aggMetricsCache } from './constants';

/**
 * Shared caching/validation primitives for FetchAggregatedMetrics consumers
 * (dashboard-new.component, metrics.component, default-layout janitor).
 *
 * Design rules (see docs/dashboard-agg-metrics-resilience-plan.md):
 * - One record shape everywhere (schemaVersion 2); legacy shapes are converted
 *   on read but NEVER trusted to ratify confirmedEmpty — the legacy
 *   _confirmedEmpty flag was written by the very bug this replaces.
 * - A response is applied to the UI only via shouldAcceptRefresh — a resolved
 *   [] never overwrites non-empty data for the same request key, because the
 *   backend cannot yet distinguish "no activity" from "aggregation gave up".
 * - Obsolete-response discarding (late Filter A after Filter B) is NOT this
 *   module's job: callers must generation-guard before consulting the matrix.
 * - Storage access AND all conversion/validation is best-effort: no failure
 *   in here may ever block the caller's API path.
 * - NO automatic retries anywhere (30s AppSync ceiling).
 */

export const AGG_CACHE_SCHEMA_VERSION = 2;

export interface AggCacheRecord<T = any> {
  schemaVersion: number;
  data: T;
  /** epoch ms when the data was fetched */
  updatedAt: number;
  /** serve without refresh until this epoch ms */
  freshUntil: number;
  /** serve as stale fallback until this epoch ms (= freshUntil + retention); janitor deletes only after */
  staleUntil: number;
  /** true only when [] was ratified by a COLD fetch on schemaVersion >= 2 */
  confirmedEmpty: boolean;
}

export interface AggCacheReadResult<T = any> {
  record: AggCacheRecord<T> | null;
  /** record exists and freshUntil >= now */
  fresh: boolean;
}

/** Minimal storage contract so specs can inject a fake store. */
export interface AggCacheStore {
  getItem<T>(key: string): Promise<T | null>;
  setItem<T>(key: string, value: T): Promise<T>;
  removeItem(key: string): Promise<void>;
}

export type AggResponseValidity = 'valid-nonempty' | 'valid-empty' | 'invalid';

/**
 * apply       — trust the response, show and cache it
 * keep-stale  — same-key non-empty data stays on screen; flag the failure
 * show-empty  — genuine (cold-ratified) empty state
 * unavailable — nothing trustworthy to show for this key: the caller must
 *               clear any other key's data from the section and show an
 *               explicit unavailable/zero state (never another filter's numbers)
 */
export type RefreshDecision = 'apply' | 'keep-stale' | 'show-empty' | 'unavailable';

/**
 * Classify a resolved FetchAggregatedMetrics response.
 * Rejections/throws never reach this — they are handled as errors by callers.
 *
 * Emptiness is judged on the FLATTENED metric values, never the envelope:
 * [{metricValues: []}] renders as zeros exactly like [] does, so both are
 * 'valid-empty' (deciding on the envelope shape recreated the zeros bug in
 * consumers without a transform, e.g. the previous-period benchmark).
 * Entries missing a name or carrying a non-finite value make the whole
 * response 'invalid' — partial garbage keeps last-good rather than rendering.
 */
export function validateAggResponse(resp: any): AggResponseValidity {
  if (!Array.isArray(resp)) {
    return 'invalid';
  }
  for (const item of resp) {
    if (!item || typeof item !== 'object' || !Array.isArray((item as any).metricValues)) {
      return 'invalid';
    }
  }
  const values: any[] = resp.flatMap(item => (item as any).metricValues);
  if (values.length === 0) {
    return 'valid-empty';
  }
  return values.every(v => v && typeof v === 'object'
    && typeof v.name === 'string' && v.name.length > 0
    && Number.isFinite(v.value))
    ? 'valid-nonempty'
    : 'invalid';
}

export interface AggMetricsKeyParts {
  accountId: string;
  fromDt?: string | null;
  toDt?: string | null;
  period?: string | null;
  metricLevel?: string | null;
  entityType?: string | null;
  entityId?: Array<string> | null;
  aggregate?: boolean | null;
  /** accepts the generated API type (Array<string | null>); nulls are normalized out */
  metricName?: string | Array<string | null> | null;
  statisticsName?: string | null;
  /** local date key (YYYY-MM-DD) so entries roll over daily */
  localDateKey: string;
  /** extra display-scope discriminator, e.g. leaderBoardPeriod or USERLEVEL */
  scope?: string | null;
}

/**
 * Canonical request key: `<prefix>_<JSON array>` — the prefix keeps janitor
 * prefix-matching working; the JSON payload makes the key collision-proof
 * (field values containing '_' cannot merge or split positions).
 * Arrays are sorted so key identity is order-independent.
 */
export function buildAggMetricsKey(prefix: string, p: AggMetricsKeyParts): string {
  const entityIds = (p.entityId || []).slice().sort();
  const metricNames = (Array.isArray(p.metricName) ? p.metricName.slice().sort() : [p.metricName])
    .filter(Boolean);
  return prefix + '_' + JSON.stringify([
    p.accountId,
    p.fromDt || '',
    p.toDt || '',
    p.period || '',
    p.metricLevel || '',
    p.entityType || 'NONE',
    entityIds,
    p.aggregate === true ? 1 : 0,
    metricNames,
    p.statisticsName || '',
    p.scope || '',
    p.localDateKey,
  ]);
}

export interface RefreshContext {
  validity: AggResponseValidity;
  /** canonical key of the request that just resolved */
  freshKey: string;
  /** canonical key of the data currently displayed for this section (null if nothing shown) */
  displayedKey: string | null;
  /** the displayed data is non-empty (real numbers on screen) */
  displayedNonEmpty: boolean;
}

/**
 * Decision matrix (plan §2a). PRECONDITION: the caller has already discarded
 * obsolete responses (generation guard) — freshKey is the CURRENT request.
 *
 * - valid-nonempty                  -> apply
 * - valid-empty, same key shown
 *     with non-empty data           -> keep-stale (manual refresh included — a
 *                                      user click doesn't make an ambiguous []
 *                                      trustworthy)
 * - valid-empty otherwise           -> show-empty (cold / nothing shown for key)
 * - invalid, same key shown
 *     with non-empty data           -> keep-stale
 * - invalid otherwise               -> unavailable (the section may be showing
 *                                      ANOTHER key's numbers — they must be
 *                                      cleared, not retained as this filter's)
 */
export function shouldAcceptRefresh(ctx: RefreshContext): RefreshDecision {
  const sameKeyNonEmptyShown =
    ctx.displayedKey !== null && ctx.displayedKey === ctx.freshKey && ctx.displayedNonEmpty;
  if (ctx.validity === 'valid-nonempty') {
    return 'apply';
  }
  if (sameKeyNonEmptyShown) {
    return 'keep-stale';
  }
  return ctx.validity === 'valid-empty' ? 'show-empty' : 'unavailable';
}

const MS_PER_MIN = 60 * 1000;

export function staleRetentionMs(): number {
  return aggMetricsCache.staleRetentionHours * 60 * MS_PER_MIN;
}

function toRecord<T>(raw: any, freshTtlMs: number): AggCacheRecord<T> | null {
  const retention = staleRetentionMs();
  if (typeof raw === 'object' && raw !== null
    && (raw as any).schemaVersion >= AGG_CACHE_SCHEMA_VERSION
    && typeof (raw as any).freshUntil === 'number'
    && typeof (raw as any).staleUntil === 'number') {
    return raw as AggCacheRecord<T>;
  }
  // Legacy conversions below: confirmedEmpty is ALWAYS false — pre-schema-v2
  // flags (incl. _confirmedEmpty) may have been written from a background []
  // during the incident this module fixes, so they cannot ratify emptiness.
  if (typeof raw === 'object' && raw !== null && typeof (raw as any).expiry === 'number'
    && 'data' in (raw as any)) {
    // legacy getOrSetCache / metrics-grid shape { expiry, data, updatedAt? }
    const updatedAt = typeof (raw as any).updatedAt === 'number'
      ? (raw as any).updatedAt
      : ((raw as any).expiry - freshTtlMs);
    return {
      schemaVersion: 1,
      data: (raw as any).data,
      updatedAt,
      freshUntil: (raw as any).expiry,
      staleUntil: (raw as any).expiry + retention,
      confirmedEmpty: false,
    };
  }
  if (typeof raw === 'object' && raw !== null && typeof (raw as any)._cacheTimestamp === 'number'
    && 'data' in (raw as any)) {
    // legacy realTimeMetrics_* shape { data, _cacheTimestamp, _confirmedEmpty }
    return {
      schemaVersion: 1,
      data: (raw as any).data,
      updatedAt: (raw as any)._cacheTimestamp,
      freshUntil: (raw as any)._cacheTimestamp + freshTtlMs,
      staleUntil: (raw as any)._cacheTimestamp + freshTtlMs + retention,
      confirmedEmpty: false,
    };
  }
  // legacy raw payload (no envelope): age unknown -> usable as stale only
  return {
    schemaVersion: 1,
    data: raw as T,
    updatedAt: 0,
    freshUntil: 0,
    staleUntil: Date.now() + retention,
    confirmedEmpty: false,
  };
}

/**
 * Best-effort read of ONE key. Converts legacy shapes; discards invalid
 * payloads and unratified empties. Every failure mode — storage read,
 * conversion, validator throw — resolves to a miss so the API path always
 * runs. Nothing is deleted on processing errors (only on affirmatively
 * unusable or stale-expired content).
 */
export async function readAggCache<T = any>(
  key: string,
  freshTtlMs: number,
  validate: (data: any) => AggResponseValidity = validateAggResponse,
  store: AggCacheStore = localforage as unknown as AggCacheStore,
): Promise<AggCacheReadResult<T>> {
  const miss: AggCacheReadResult<T> = { record: null, fresh: false };
  let raw: any;
  try {
    raw = await store.getItem(key);
  } catch {
    return miss; // storage failure must fall through to the API path
  }
  if (raw === null || raw === undefined) {
    return miss;
  }
  try {
    const now = Date.now();
    const record = toRecord<T>(raw, freshTtlMs);
    if (!record) {
      return miss;
    }
    const validity = validate(record.data);
    const dataUsable = validity === 'valid-nonempty'
      || (validity === 'valid-empty' && record.confirmedEmpty && record.schemaVersion >= AGG_CACHE_SCHEMA_VERSION);
    if (!dataUsable || record.staleUntil < now) {
      try { await store.removeItem(key); } catch { /* best-effort */ }
      return miss;
    }
    return { record, fresh: record.freshUntil >= now };
  } catch {
    // Conversion/validator threw: unusable to us, but do NOT delete — an
    // exception is not evidence the stored data is bad (janitor lesson).
    return miss;
  }
}

/**
 * Dual-key read for rollout (plan review P0-4): canonical key first, then the
 * EXACT legacy key production wrote before this change. A valid NON-EMPTY
 * legacy hit is migrated under the canonical key (original updatedAt kept, so
 * freshness stays honest) and the legacy entry removed. Legacy empties are
 * never migrated.
 */
export async function readAggCacheDualKey<T = any>(
  canonicalKey: string,
  legacyKey: string | null,
  freshTtlMs: number,
  validate: (data: any) => AggResponseValidity = validateAggResponse,
  store: AggCacheStore = localforage as unknown as AggCacheStore,
): Promise<AggCacheReadResult<T>> {
  const canonical = await readAggCache<T>(canonicalKey, freshTtlMs, validate, store);
  if (canonical.record || !legacyKey) {
    return canonical;
  }
  const legacy = await readAggCache<T>(legacyKey, freshTtlMs, validate, store);
  if (!legacy.record) {
    return legacy;
  }
  // readAggCache already discarded invalid/empty legacy content — this is a
  // valid non-empty survivor. Migrate it under the canonical key as-is.
  const migrated: AggCacheRecord<T> = { ...legacy.record, schemaVersion: AGG_CACHE_SCHEMA_VERSION };
  try {
    await store.setItem(canonicalKey, migrated);
    await store.removeItem(legacyKey);
  } catch { /* best-effort */ }
  return { record: migrated, fresh: legacy.fresh };
}

/**
 * Best-effort validated write. Refuses to persist:
 * - invalid payloads,
 * - empty payloads not ratified as confirmedEmpty (an unratified [] in the
 *   cache would resurrect the zeros bug on the next read),
 * and normalizes confirmedEmpty to false for non-empty data.
 * Returns whether a write happened. Never throws.
 */
export async function writeAggCache<T = any>(
  key: string,
  data: T,
  freshTtlMs: number,
  confirmedEmpty: boolean = false,
  validate: (data: any) => AggResponseValidity = validateAggResponse,
  store: AggCacheStore = localforage as unknown as AggCacheStore,
): Promise<boolean> {
  try {
    const validity = validate(data);
    if (validity === 'invalid') {
      return false;
    }
    if (validity === 'valid-empty' && !confirmedEmpty) {
      return false;
    }
    const now = Date.now();
    const freshUntil = now + freshTtlMs;
    const record: AggCacheRecord<T> = {
      schemaVersion: AGG_CACHE_SCHEMA_VERSION,
      data,
      updatedAt: now,
      freshUntil,
      staleUntil: freshUntil + staleRetentionMs(),
      confirmedEmpty: validity === 'valid-empty',
    };
    await store.setItem(key, record);
    return true;
  } catch {
    return false;
  }
}

export type AggLoadState =
  | 'fresh' | 'stale' | 'empty' | 'kept-stale' | 'unavailable'
  /** a newer generation owns the screen — ignore entirely */
  | 'obsolete'
  /**
   * The refresh intent was already consumed by an exact-key attempt AND there
   * is nothing cached to (re)serve. The outcome of that attempt — success or
   * failure — was ALREADY rendered when it settled; this pass must not infer
   * a different one (a hung cache write after a successful response must not
   * become a false failure strip). Callers ignore it, exactly like obsolete.
   */
  | 'intent-consumed';

export interface AggLoadResult<T = any> {
  state: AggLoadState;
  /** populated for fresh/stale/empty; null otherwise */
  data: T | null;
  updatedAt: number | null;
  /**
   * cache   — stored data reapplied without touching the network. Callers must
   *           NOT clear failure affordances on it: nothing recovered.
   * network — a live response (or a live failure decision).
   */
  source: 'cache' | 'network';
  key: string;
}

export interface AggLoadRequest<T = any> {
  /** canonical request key (buildAggMetricsKey) */
  key: string;
  /** exact pre-rollout key, for one-time stale migration */
  legacyKey?: string | null;
  fetcher: () => Promise<any>;
  freshTtlMs: number;
  /** force refresh: bypass the fresh early-return but keep the entry as fallback */
  preferNetwork?: boolean;
  /**
   * Epoch ms of the manual-refresh click this load serves (0/absent = none).
   * One refresh intent permits AT MOST ONE network attempt per exact key: an
   * exact-key attempt that settles at-or-after this moment — success,
   * ambiguous empty, invalid response, or rejection — consumes the intent,
   * and no further network is attempted for this key on its behalf. A failed
   * attempt therefore never triggers a second call (that would be an
   * automatic retry). Attempts for key A never satisfy key B.
   */
  refreshIntentAt?: number;
  /** generation check — false once a newer pass owns the screen */
  isCurrent: () => boolean;
  /** what this section currently displays, for the empty/invalid heuristic */
  displayed: () => { key: string | null; nonEmpty: boolean };
  /** raw-response validity (defaults to validateAggResponse) */
  validateResponse?: (d: any) => AggResponseValidity;
  /** cached-shape validity, when the cache stores a transformed shape */
  validateCached?: (d: any) => AggResponseValidity;
  /** raw response -> cached/displayed shape (defaults to identity) */
  transform?: (raw: any) => T;
  /** background-revalidation results arrive here (never 'obsolete') */
  onUpdate?: (res: AggLoadResult<T>) => void;
  /** test injection */
  store?: AggCacheStore;
}

/**
 * The single load-with-stale-fallback operation shared by every aggregated-
 * metrics consumer. It owns: dual-key cache read + legacy migration,
 * fresh/stale serving, background revalidation with per-key in-flight dedup,
 * the empty/invalid decision matrix, validated cache writes, and storage-error
 * fallback. Callers own: key building, generation counting, and rendering.
 *
 * THE INVARIANT — enforced here and nowhere else:
 *   1. never apply a response that doesn't belong to the current generation;
 *   2. never replace same-key known-good data with an ambiguous empty/error;
 *   3. never retain different-key data as if it answered the current request.
 */
export class AggMetricsLoader {
  /**
   * One network call per key at a time, shared as a PROMISE — every requester
   * (including a newer generation arriving while an older refresh is still in
   * flight) awaits the same response but runs its OWN decision with its own
   * context. Dedup must never starve the current generation: the old
   * requester's result goes obsolete, the new one still gets the data.
   */
  private inFlightFetches = new Map<string, Promise<any>>();
  /** When the last network attempt for a key SETTLED (success or failure). */
  private lastAttemptSettledAt = new Map<string, number>();

  /** Forget in-flight bookkeeping (account switch / reset). Entries self-clean
   * via an identity-checked finalizer, so an old fetch finishing after reset
   * can never delete a newer entry. */
  reset(): void {
    this.inFlightFetches.clear();
    this.lastAttemptSettledAt.clear();
  }

  async load<T = any>(req: AggLoadRequest<T>): Promise<AggLoadResult<T>> {
    const validateCached = req.validateCached ?? req.validateResponse ?? validateAggResponse;
    const read = await readAggCacheDualKey<T>(
      req.key, req.legacyKey ?? null, req.freshTtlMs, validateCached, this.storeOf(req));
    if (!this.isCurrent(req)) {
      return this.result('obsolete', req.key);
    }

    // Refresh-intent accounting (per exact key): once an exact-key attempt has
    // settled at-or-after the click, this load must not spend more network on
    // that intent — a settled FAILURE especially, since refetching after it
    // would be an automatic retry against a struggling 30s backend.
    const intentAt = req.refreshIntentAt ?? 0;
    const intentConsumed = intentAt > 0 &&
      (this.lastAttemptSettledAt.get(req.key) ?? 0) >= intentAt;

    if (read.record) {
      // Serve cached (fresh OR stale) immediately — never blank while
      // same-key data exists. Stale or preferNetwork revalidates in the
      // background; the awaited return is always instant on a cache hit.
      const shouldRevalidate = !intentConsumed && (!read.fresh || req.preferNetwork === true);
      if (shouldRevalidate) {
        this.revalidateInBackground(req, true);
      }
      return {
        state: read.fresh ? 'fresh' : 'stale',
        data: read.record.data,
        updatedAt: read.record.updatedAt,
        source: 'cache',
        key: req.key,
      };
    }
    if (intentConsumed) {
      // Cold AND the intent already consumed. "No cache" does NOT imply the
      // attempt failed — a successful response's fire-and-forget write may
      // have failed or still be pending. Whatever that attempt produced was
      // rendered when it settled; report a typed no-op instead of inferring
      // kept-stale/unavailable (which would raise a false failure strip over
      // a successful refresh). Fetching again is equally off the table — that
      // would be the automatic retry we ban.
      return this.result('intent-consumed', req.key);
    }
    // Cold: nothing to show for this key — fetch and wait.
    return this.fetchAndDecide(req, false);
  }

  private storeOf(req: AggLoadRequest<any>): AggCacheStore {
    return req.store ?? (localforage as unknown as AggCacheStore);
  }

  private result<T>(state: AggLoadState, key: string): AggLoadResult<T> {
    return { state, data: null, updatedAt: null, source: 'network', key };
  }

  /** Caller closures may throw; a throwing isCurrent() means "not current". */
  private isCurrent(req: AggLoadRequest<any>): boolean {
    try { return req.isCurrent(); } catch { return false; }
  }

  /** A throwing displayed() reads as "nothing shown" — the safe default. */
  private displayedOf(req: AggLoadRequest<any>): { key: string | null; nonEmpty: boolean } {
    try { return req.displayed(); } catch { return { key: null, nonEmpty: false }; }
  }

  /** Share one network call per key; both cold loads and revalidations join it. */
  private sharedFetch(req: AggLoadRequest<any>): Promise<any> {
    let pending = this.inFlightFetches.get(req.key);
    if (!pending) {
      pending = Promise.resolve().then(() => req.fetcher());
      const entry = pending;
      entry.catch(() => { }).then(() => {
        // Identity check: reset() or a replacement may own the slot by now.
        if (this.inFlightFetches.get(req.key) === entry) {
          this.inFlightFetches.delete(req.key);
        }
      });
      this.inFlightFetches.set(req.key, pending);
    }
    return pending;
  }

  private revalidateInBackground<T>(req: AggLoadRequest<T>, hadRecord: boolean): void {
    this.fetchAndDecide(req, hadRecord)
      .then(res => {
        // Re-check right at the delivery boundary — intent may have moved on
        // between the decision and this microtask.
        if (res.state !== 'obsolete' && this.isCurrent(req) && req.onUpdate) {
          req.onUpdate(res);
        }
      })
      .catch(() => { /* fetchAndDecide never rejects; also swallows onUpdate throws */ });
  }

  private async fetchAndDecide<T>(req: AggLoadRequest<T>, hadRecord: boolean): Promise<AggLoadResult<T>> {
    let raw: any;
    try {
      raw = await this.sharedFetch(req);
      this.lastAttemptSettledAt.set(req.key, Date.now());
    } catch {
      // A settled FAILURE also consumes any refresh intent for this key.
      this.lastAttemptSettledAt.set(req.key, Date.now());
      if (!this.isCurrent(req)) {
        return this.result('obsolete', req.key);
      }
      const d = this.displayedOf(req);
      // Rejection: same-key known-good survives; anything else (cold, or
      // another key's numbers on screen) is unavailable for this key.
      return (d.key === req.key && d.nonEmpty)
        ? this.result('kept-stale', req.key)
        : this.result('unavailable', req.key);
    }
    if (!this.isCurrent(req)) {
      return this.result('obsolete', req.key);
    }

    // Decide on the EFFECTIVE validity — the shape the component will render
    // and cache, not the raw envelope. [{metricValues: []}] transforms to []
    // and MUST count as empty even though the raw outer array is non-empty
    // (deciding on the raw shape recreated the non-empty→zeros bug). A
    // throwing or invalid transform counts as invalid.
    const validateResponse = req.validateResponse ?? validateAggResponse;
    const validateCached = req.validateCached ?? validateResponse;
    const transform = req.transform ?? ((r: any) => r as T);
    let data: T | null = null;
    let validity: AggResponseValidity;
    try {
      if (validateResponse(raw) === 'invalid') {
        validity = 'invalid';
      } else {
        data = transform(raw);
        validity = validateCached(data);
      }
    } catch {
      validity = 'invalid';
    }

    const d = this.displayedOf(req);
    const decision = shouldAcceptRefresh({
      validity,
      freshKey: req.key,
      displayedKey: d.key,
      displayedNonEmpty: d.nonEmpty,
    });
    if (decision === 'keep-stale' || decision === 'unavailable') {
      return this.result(decision === 'keep-stale' ? 'kept-stale' : 'unavailable', req.key);
    }
    const now = Date.now();
    // Persistence is fire-and-forget: writing a response under its own exact
    // key is harmless even if intent moves on, and UI delivery must not sit
    // behind IndexedDB (the generation could change during an awaited write).
    // writeAggCache itself refuses unratified empties, so a background
    // show-empty (hadRecord=true -> confirmedEmpty=false) is displayed but
    // never persisted as truth.
    void writeAggCache(req.key, data as T, req.freshTtlMs,
      decision === 'show-empty' && !hadRecord, validateCached, this.storeOf(req));
    if (!this.isCurrent(req)) {
      return this.result('obsolete', req.key);
    }
    return { state: decision === 'apply' ? 'fresh' : 'empty', data, updatedAt: now, source: 'network', key: req.key };
  }
}

/**
 * Janitor predicate for the standardized schema: recognizable v2 records are
 * deletable ONLY past staleUntil. Returns null when the value is not a v2
 * record (legacy patterns apply instead).
 */
export function isExpiredAggCacheRecord(raw: any, now: number): boolean | null {
  if (typeof raw === 'object' && raw !== null
    && (raw as any).schemaVersion >= AGG_CACHE_SCHEMA_VERSION
    && typeof (raw as any).staleUntil === 'number') {
    return (raw as any).staleUntil < now;
  }
  return null;
}
